import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import ChipDropdownMenu, { ChipDropdownMenuProps } from "../OrderSearch/ChipDropdownMenu.tsx";
import { FilterType } from "../OrderSearch/filterType.ts";
import { models } from "types/api/viewModels.ts";
import { getAssigneeName, getProductLine, isCompletedRequestStatus } from "./customQuoteUtils.ts";

interface CustomQuoteRequestDetails extends ChipDropdownMenuProps {
	dataTestId: string;
	isVisible: boolean;
	GTMIdentifier: string;
}

export type ActiveView = "New" | "Completed";

interface NewRequestTableFilterProps {
	filterOptions: FilterOpts;
	setFilterOptions: Dispatch<SetStateAction<FilterOpts>>;
	requests: models["CustomQuoteViewModel"][];
	activeView: ActiveView;
}

const CustomQuoteRequestTableFilter = ({
	filterOptions,
	setFilterOptions,
	requests,
	activeView
}: NewRequestTableFilterProps) => {
	const newRequestAssignees: string[] = useMemo(
		() =>
			requests
				.filter((request) => !isCompletedRequestStatus(request))
				.flatMap((request) => getAssigneeName(request))
				.filter((name): name is string => !!name),
		[requests]
	);

	const completedRequestAssignees: string[] = useMemo(
		() =>
			requests
				.filter((request) => isCompletedRequestStatus(request))
				.flatMap((request) => getAssigneeName(request))
				.filter((name): name is string => !!name),
		[requests]
	);

	const filteredNewAssignees: any = useMemo(() => [...new Set(newRequestAssignees)], [newRequestAssignees]);
	const filteredCompletedAssignees: any = useMemo(
		() => [...new Set(completedRequestAssignees)],
		[completedRequestAssignees]
	);

	const newRequestProductLines: string[] = useMemo(
		() =>
			requests
				.filter((request) => !isCompletedRequestStatus(request))
				.map((request) => getProductLine(request))
				.filter((pl): pl is string => !!pl),
		[requests]
	);

	const completedRequestProductLines: string[] = useMemo(
		() =>
			requests
				.filter((request) => isCompletedRequestStatus(request))
				.map((request) => getProductLine(request))
				.filter((pl): pl is string => !!pl),
		[requests]
	);

	const filteredNewProductLines: string[] = useMemo(
		() => [...new Set(newRequestProductLines)],
		[newRequestProductLines]
	);
	const filteredCompletedProductLines: string[] = useMemo(
		() => [...new Set(completedRequestProductLines)],
		[completedRequestProductLines]
	);

	const onFilterChange = useCallback(
		(id: string, values: string[]) => {
			setFilterOptions((prev: FilterOpts) => ({
				...prev,
				[id]: values
			}));
		},
		[setFilterOptions]
	);

	const customQuoteRequestDetails: CustomQuoteRequestDetails[] = useMemo(() => {
		const filterConfig: CustomQuoteRequestDetails[] = [
			{
				selectedItems: filterOptions?.quotedBy ?? [],
				onChange: (v: string[]) => onFilterChange("quotedBy", v),
				filterType: FilterType.QUOTED_BY,
				items: (activeView === "New" ? filteredNewAssignees : filteredCompletedAssignees) ?? [],
				dataTestId: `${activeView}-request-filter-chip`,
				isSearchable: true,
				hasWarning: false,
				children: undefined,
				isVisible: true,
				GTMIdentifier: `${activeView}-request-filter-chip`
			},
			{
				selectedItems: filterOptions?.productLines ?? [],
				onChange: (v: string[]) => onFilterChange("productLines", v),
				filterType: FilterType.QUOTE_PRODUCT_LINE,
				items: (activeView === "New" ? filteredNewProductLines : filteredCompletedProductLines) ?? [],
				dataTestId: `${activeView}-request-product-line-filter-chip`,
				isSearchable: true,
				hasWarning: false,
				children: undefined,
				isVisible: true,
				GTMIdentifier: `${activeView}-request-product-line-filter-chip`
			}
		];

		return filterConfig;
	}, [
		filterOptions?.quotedBy,
		filterOptions?.productLines,
		filteredNewAssignees,
		filteredCompletedAssignees,
		filteredNewProductLines,
		filteredCompletedProductLines,
		activeView,
		onFilterChange
	]);

	return (
		<div>
			{customQuoteRequestDetails?.map(
				(request) =>
					request.isVisible && (
						<ChipDropdownMenu
							key={request.filterType}
							selectedItems={request.selectedItems}
							onChange={request.onChange}
							filterType={request.filterType}
							items={request.items}
							dataTestId={request.dataTestId}
							hasWarning={request.hasWarning}
							isSearchable={request.isSearchable}
							GTMIdentifier={request.GTMIdentifier}
						>
							{request.children}
						</ChipDropdownMenu>
					)
			)}
		</div>
	);
};

export default CustomQuoteRequestTableFilter;
